import React, { useEffect, useState } from 'react';
import { IUser } from '../../types';
import { AuthServices } from '../../lib/services';
import { IAuthContext } from './types';
import { UserController } from '../../lib/controllers/user.ctrl';
import { UserRoleEnum } from '../../enums';
import { setUser as setSentryUser } from '@sentry/react';

export const AuthContext = React.createContext<IAuthContext>({
    isAuthenticated: false,
    isAuthLoaded: false,
    isUserAdmin: false,
    signinFromTempToken: async () => {},
    initAppSession: async (): Promise<void> => {},
    signOut: async (): Promise<void> => {}
});

export const AuthProvider = ({ children }: any) => {
    const [user, setUser] = React.useState<IUser|undefined>();
    const [userRoles, setUserRoles] = React.useState<UserRoleEnum[]>();
    const [isUserAdmin, setUserIsAdmin] = React.useState(false);
    const [jwtToken, setJwtToken] = useState<string|undefined>(undefined);
    const [isAuthenticated, setIsAuthenticated] = React.useState(false);
    const [isAuthLoaded, setIsAuthLoaded] = React.useState(false);

    useEffect(() => {
        loadUserSession();
    }, [jwtToken]);

    const loadUserSession = async () => {
        setIsAuthenticated(false);
        if (!jwtToken) {
            const storedAccessToken = AuthServices.getAccessToken();
            if (!storedAccessToken) {
                setIsAuthLoaded(true);
                setUserPermissions();
                return;
            }
            setJwtToken(storedAccessToken);
        }

        const userService = new UserController();
        const user = await userService.getMe();
        setUser(user);
        setUserPermissions();
        setIsAuthenticated(true);
        setIsAuthLoaded(true);
        setSentryUser({
            id: user.id,
            email: user.email,
            name: user.firstname + ' ' + user.lastname
        });
    }

    const setUserPermissions = () => {
        const roles = AuthServices.getAccessTokenUserRoles();
        setUserRoles(roles);
        setUserIsAdmin(roles.includes(UserRoleEnum.ADMIN));
    }

    const signinFromTempToken = async (data: { state: string, source: 'planning-center', token: string, status: string }): Promise<any> => {
        setIsAuthenticated(false);
        try {

            if (data.status === 'user_waiting_for_approval') {
                return;
            }
            await AuthServices.signin(data);
            const currentJwtToken = AuthServices.getAccessToken();
            setJwtToken(currentJwtToken);
        } catch {
            setIsAuthenticated(false);
        }
    }

    const initAppSession = async (): Promise<void> => {
        setIsAuthLoaded(false);
        setJwtToken(AuthServices.getAccessToken());
    }

    const signOut = async () => {
        await AuthServices.signOut();
        setJwtToken(undefined);
        setUserRoles([]);
        setUserIsAdmin(false);
        setUser(undefined);
        setIsAuthenticated(false);
        setSentryUser(null);
    }

    return <AuthContext.Provider value={{
        // session,
        user,
        isAuthenticated,
        isAuthLoaded,
        isUserAdmin,
        userRoles,
        signinFromTempToken,
        initAppSession,
        signOut
    }}>
        {children}
    </AuthContext.Provider>
}
